import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useRecordPayment } from '@/hooks/invoicing';

export const PaymentDialog = ({
  open, onOpenChange, invoiceId, balanceDue,
}: { open: boolean; onOpenChange: (o: boolean) => void; invoiceId: string; balanceDue: number; }) => {
  const [entered, setEntered] = useState(balanceDue.toFixed(2));
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const recordPayment = useRecordPayment();

  const enteredNum = Number(entered) || 0;
  const applied = Math.min(enteredNum, balanceDue);
  const change = method === 'cash' ? Math.max(0, enteredNum - balanceDue) : 0;

  const submit = async () => {
    await recordPayment.mutateAsync({
      invoiceId, kind: 'payment',
      enteredAmount: enteredNum,
      appliedAmount: method === 'cash' ? applied : enteredNum,
      changeGiven: change, method,
      transactionReference: reference || undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">Balance due: ${balanceDue.toFixed(2)}</div>
          <div className="space-y-2">
            <Label>Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="ach">ACH</SelectItem>
                <SelectItem value="financing">Financing</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Entered amount</Label>
            <Input type="number" step="0.01" value={entered} onChange={(e) => setEntered(e.target.value)} />
          </div>
          {method === 'cash' && change > 0 && (
            <div className="text-sm">Change due: <span className="font-semibold">${change.toFixed(2)}</span></div>
          )}
          <div className="space-y-2">
            <Label>Reference (optional)</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Check #, last 4, etc." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={recordPayment.isPending || enteredNum <= 0}>
            {recordPayment.isPending ? 'Saving…' : 'Record'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
